import {
    getOwnedNodes,
    NodeOwnerLikeNode,
    isNamedNode,
    RigidRelation,
    ConfigGroup,
    LodType,
    getNodeOwner,
    SceneNodes,
    TemplateNode,
    isNodeOwnerLikeNode,
    SwitchNode,
    isValueLikeNode,
    isTemplateContainer,
    ObjectPath,
} from "@cm/template-nodes"
import {CdkTreeModule, NestedTreeControl} from "@angular/cdk/tree"
import {Component, DestroyRef, OnInit, computed, effect, forwardRef, inject, input, signal, ChangeDetectionStrategy} from "@angular/core"
import {MatTreeNestedDataSource} from "@angular/material/tree"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {TemplateTreeItemComponent} from "@template-editor/components/template-tree-item/template-tree-item.component"
import {TemplateTreeAddComponent} from "@template-editor/components/template-tree-add/template-tree-add.component"
import {pairwise} from "rxjs"
import {TemplateNodeDragService, TemplateDropTarget, DroppableComponent, DropPosition} from "@app/template-editor/services/template-node-drag.service"
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop"
import {Hotkeys} from "@app/common/services/hotkeys/hotkeys.service"
import {TemplateNodeClipboardService} from "@app/template-editor/services/template-node-clipboard.service"
import {TemplateTreeFillerComponent} from "@app/template-editor/components/template-tree-filler/template-tree-filler.component"
import {SceneAggregationMode} from "@app/template-editor/components/template-tree-view-type-selector/template-tree-view-type-selector.component"
import {createTemplateTreeNode, getNodeChildren, getTemplateTreeNodeId, hasChildren} from "@app/template-editor/components/template-tree/template-tree-types"
import type {TemplateTreeNode} from "@app/template-editor/components/template-tree/template-tree-types"

export type {TemplateTreeNode}
export {hasChildren, getTemplateTreeNodeId}

const getAllNodes = (treeControl: NestedTreeControl<TemplateTreeNode, string>, dataSource: MatTreeNestedDataSource<TemplateTreeNode>): TemplateTreeNode[] => {
    const result: TemplateTreeNode[] = []
    const stack = [...dataSource.data]
    while (stack.length > 0) {
        const node = stack.pop()!
        result.push(node)
        if (hasChildren(node)) stack.push(...treeControl.getDescendants(node))
    }
    return result
}

const isTemplateTreeItemDropTarget = (
    dropTarget: TemplateDropTarget<DroppableComponent>,
): dropTarget is TemplateDropTarget<TemplateTreeItemComponent> & {position: DropPosition} => {
    return dropTarget.component instanceof TemplateTreeItemComponent && typeof dropTarget.position === "string"
}

const getTargetNode = (dropTarget: TemplateDropTarget<DroppableComponent> | null) => {
    if (!dropTarget || !isTemplateTreeItemDropTarget(dropTarget)) return null
    const {component, position} = dropTarget
    const node = component.$treeNode()

    return position === "inside" ? node : node.parent
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-tree",
    templateUrl: "./template-tree.component.html",
    styleUrl: "./template-tree.component.scss",
    imports: [CdkTreeModule, forwardRef(() => TemplateTreeItemComponent), TemplateTreeAddComponent, TemplateTreeFillerComponent],
})
export class TemplateTreeComponent implements OnInit {
    readonly sceneManagerService = inject(SceneManagerService)
    private readonly destroyRef = inject(DestroyRef)
    private readonly hotkeys = inject(Hotkeys)
    private readonly clipboardService = inject(TemplateNodeClipboardService)

    readonly $showSceneNodes = input.required<boolean>({alias: "showSceneNodes"})
    readonly $sceneAggregationMode = input.required<SceneAggregationMode>({alias: "sceneAggregationMode"})
    readonly $rootNode = computed(() => createTemplateTreeNode(null, this.sceneManagerService.$templateGraph()))
    readonly $children = signal<TemplateTreeItemComponent[]>([])
    registerChild = (child: TemplateTreeItemComponent) => this.$children.update((children) => [...children, child])
    unregisterChild = (child: TemplateTreeItemComponent) => this.$children.update((children) => children.filter((x) => x !== child))

    readonly $sceneNodeInfo = computed<SceneNodes.SceneNode[] | undefined>(() => {
        if (!this.$showSceneNodes()) return undefined

        const expandPath = (path: ObjectPath): ObjectPath => {
            return path
                .map((item) => {
                    const result: ObjectPath = []
                    let node: TemplateNode | null = item
                    while (node && node instanceof TemplateNode) {
                        if (node === item || isNodeOwnerLikeNode(node)) result.unshift(node)
                        node = getNodeOwner(node)
                    }
                    return result
                })
                .flat()
        }

        const scene = this.sceneManagerService.$scene()

        return scene
            .filter(
                (sceneNode) =>
                    SceneNodes.Camera.is(sceneNode) ||
                    SceneNodes.SceneOptions.is(sceneNode) ||
                    SceneNodes.RenderPostProcessingSettings.is(sceneNode) ||
                    SceneNodes.Mesh.is(sceneNode) ||
                    SceneNodes.MeshCurveControl.is(sceneNode) ||
                    SceneNodes.Seam.is(sceneNode) ||
                    SceneNodes.Environment.is(sceneNode) ||
                    SceneNodes.DataObjectReference.is(sceneNode),
            )
            .map((sceneNode) => ({...sceneNode, path: expandPath(sceneNode.path)}))
    })

    treeControl = new NestedTreeControl<TemplateTreeNode, string>(getNodeChildren, {trackBy: (node) => node.node.instanceId})
    readonly $dataSource = signal(new MatTreeNestedDataSource<TemplateTreeNode>())
    readonly $highlightedNodes = signal<Map<string, Set<string>>>(new Map())
    readonly $hasChildren = signal(hasChildren)
    needsAddWidget = (node: TemplateTreeNode) => {
        const templateNode = node.node
        return templateNode instanceof ConfigGroup || templateNode instanceof SwitchNode
    }

    readonly drag = inject(TemplateNodeDragService)
    private dropTarget$ = toObservable(this.drag.$dropTarget)

    constructor() {
        effect(() => this.refreshTree())

        effect(() => {
            const allNodes = getAllNodes(this.treeControl, this.$dataSource())

            const selectedTreeNodes = this.sceneManagerService
                .$selectedRootNodeParts()
                .map((selectedNodePart) =>
                    allNodes.find((node) => node.node === selectedNodePart.templateNode && (node.parent === null || isNodeOwnerLikeNode(node.parent.node))),
                )
                .filter((x): x is TemplateTreeNode<NodeOwnerLikeNode> => x !== undefined)

            selectedTreeNodes.forEach((selectedTreeNode) => {
                let {parent} = selectedTreeNode
                while (parent) {
                    this.treeControl.expand(parent)
                    parent = parent.parent
                }
            })
        })
    }

    ngOnInit() {
        this.dropTarget$.pipe(takeUntilDestroyed(this.destroyRef), pairwise()).subscribe(([previousDropTarget, dropTarget]) => {
            const previousTargetNode = getTargetNode(previousDropTarget)
            const targetNode = getTargetNode(dropTarget)

            if (previousTargetNode === targetNode) return

            if (previousTargetNode) this.highlightNode(previousTargetNode, "nodeDrop", false)
            if (targetNode) this.highlightNode(targetNode, "nodeDrop", true)
        })

        this.drag.draggedItem$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({dragSource, dropTarget}) => {
            if (isTemplateTreeItemDropTarget(dropTarget)) {
                dropTarget.component.moveNodeTo(dragSource, dropTarget.position)

                const targetNode = getTargetNode(dropTarget)
                if (targetNode) this.treeControl.expand(targetNode)
            }
        })

        this.sceneManagerService.templateTreeChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((differences) => {
            for (const [node, difference] of differences.modifiedNodes) {
                if (
                    ((isNodeOwnerLikeNode(node) || node instanceof SwitchNode) && difference.find((x) => x.path === "nodes")) ||
                    isTemplateContainer(node) ||
                    (isNamedNode(node) && difference.find((x) => x.path === "name") !== undefined) ||
                    (node instanceof RigidRelation && difference.find((x) => x.path === "targetA" || x.path === "targetB")) ||
                    (isValueLikeNode(node) && difference.find((x) => x.path === "value")) ||
                    (node instanceof LodType && difference.find((x) => x.path === "lodType"))
                ) {
                    this.refreshTree()
                    return
                }
            }
        })

        this.sceneManagerService.templateSwapped$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.$highlightedNodes.set(new Map())
        })

        this.hotkeys
            .addShortcut("undo")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.sceneManagerService.undo())
        this.hotkeys
            .addShortcut("redo")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.sceneManagerService.redo())
        this.hotkeys
            .addShortcut("copy")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.copy())
        this.hotkeys
            .addShortcut("paste")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.paste())
        this.hotkeys
            .addShortcut("delete")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.sceneManagerService.deleteNodes(this.sceneManagerService.$selectedRootNodeParts()))
    }

    private refreshTree() {
        const templateGraph = this.sceneManagerService.$templateGraph()
        const rootNode = this.$rootNode()
        this.$dataSource().data = getOwnedNodes(templateGraph).map((x) => createTemplateTreeNode(rootNode, x))
        this.treeControl.expand(rootNode)
    }

    highlightNode(node: TemplateTreeNode, highlightId: string, value: boolean) {
        const id = getTemplateTreeNodeId(node)

        this.$highlightedNodes.update((highlightedNodes) => {
            if (value) {
                const newHighlightedNodes = new Map(highlightedNodes)
                if (newHighlightedNodes.has(id)) {
                    const ids = newHighlightedNodes.get(id)!
                    ids.add(highlightId)
                } else newHighlightedNodes.set(id, new Set([highlightId]))

                return newHighlightedNodes
            } else {
                if (!highlightedNodes.has(id)) return highlightedNodes
                const highlightIds = highlightedNodes.get(id)!
                if (!highlightIds.has(highlightId)) return highlightedNodes

                const newHighlightedNodes = new Map(highlightedNodes)
                highlightIds.delete(highlightId)
                if (highlightIds.size === 0) newHighlightedNodes.delete(id)

                return newHighlightedNodes
            }
        })
    }

    isHighlighted(node: TemplateTreeNode, highlightId?: string) {
        const highlightIds = this.$highlightedNodes().get(getTemplateTreeNodeId(node))
        if (!highlightIds) return false
        if (highlightId) return highlightIds.has(highlightId)
        return highlightIds.size > 0
    }

    private copy() {
        const selectedNodes = this.sceneManagerService.$selectedRootNodeParts().map((selectedNodePart) => selectedNodePart.templateNode)
        this.clipboardService.copy(selectedNodes)
    }

    private paste() {
        if (!this.clipboardService.$valid()) return

        const selectedNodes = this.sceneManagerService.$selectedRootNodeParts().map((selectedNodePart) => selectedNodePart.templateNode)
        const nodeOwners = new Set(
            selectedNodes
                .map((selectedNode) => (isNodeOwnerLikeNode(selectedNode) ? selectedNode : undefined))
                .filter((x): x is NodeOwnerLikeNode => x !== undefined),
        )

        this.sceneManagerService.modifyTemplateGraph((templateGraph) => {
            if (nodeOwners.size === 0) nodeOwners.add(templateGraph)

            nodeOwners.forEach((nodeOwner) => {
                this.clipboardService.pasteDuplicates().forEach((node) => nodeOwner.parameters.nodes.addEntry(node))
            })
        })
    }
}
